// ForgeUltraFooter — legal disclaimer shown on every authenticated
// page.
//
// Mounted once from <Layout />, so every page that composes the
// standard app shell inherits it automatically. This guarantees two
// things legal cares about:
//   1. We cannot ship a new page without the disclaimer.
//   2. Copy changes happen in exactly one file.
//
// Content is data-driven: to amend, edit the DISCLAIMER_SECTIONS
// array below — the markup will regenerate itself.

interface DisclaimerSection {
    title: string;
    body: string;
}

const DISCLAIMER_SECTIONS: DisclaimerSection[] = [
    {
        title: 'Investment Risk & Performance',
        body:
            'Investment in financial instruments involves a high degree of risk. ' +
            'The value of investments can fluctuate, and investors may lose some ' +
            'or all of their principal. Past performance is not indicative of ' +
            'future results. Any historical data or backtested simulations ' +
            'provided by Forge Ultra are for illustrative purposes only.',
    },
    {
        title: 'No Investment Advice',
        body:
            'All content, tools, and analysis provided by Forge Ultra and ' +
            'Ki-Wealth.com are for educational and informational purposes only. ' +
            'Nothing on this platform constitutes professional investment, legal, ' +
            'or tax advice. Forge Ultra does not provide personalized investment ' +
            'recommendations or act as a fiduciary in any capacity.',
    },
    {
        title: 'Professional Consultation Recommended',
        body:
            'We strongly recommend that you consult with a certified financial ' +
            'advisor or qualified professional before making any investment ' +
            'decisions. You are solely responsible for evaluating the merits and ' +
            'risks associated with the use of any information or tools provided ' +
            'by this platform.',
    },
    {
        title: 'Data Accuracy & Third-Party Sources',
        body:
            'Analysis performed within Forge Ultra relies on raw data from ' +
            'third-party providers, including but not limited to FRED (Federal ' +
            'Reserve Economic Data), FMPrep, the European Central Bank (ECB), ' +
            'and publicly-listed companies. While we utilize sources we believe ' +
            'to be reliable, Ki-Wealth.com does not guarantee the accuracy, ' +
            'completeness, or timeliness of the data provided by these third ' +
            'parties. Forge Ultra shall not be held liable for any errors, ' +
            'omissions, or losses resulting from the use of this data.',
    },
];

export const ForgeUltraFooter = () => {
    const year = new Date().getFullYear();

    return (
        <footer
            aria-label="Legal disclaimer"
            role="contentinfo"
            className="mt-16 border-t border-slate-300 dark:border-slate-800 bg-slate-100/60 dark:bg-slate-950/50"
        >
            <div className="px-4 sm:px-6 lg:px-8 py-10 max-w-5xl mx-auto">
                {/* Section heading — small, all-caps, flanked by hairlines
                    so it reads as a formal legal notice rather than a
                    marketing block. */}
                <div className="flex items-center gap-4 mb-8">
                    <span
                        className="h-px flex-1 bg-slate-300 dark:bg-slate-800"
                        aria-hidden
                    />
                    <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500 dark:text-slate-400">
                        Legal Disclaimer
                    </span>
                    <span
                        className="h-px flex-1 bg-slate-300 dark:bg-slate-800"
                        aria-hidden
                    />
                </div>

                {/* Using <dl> instead of generic divs so assistive tech
                    reads each item as a term + description pair — which
                    is exactly what a legal section with headings is. */}
                <dl className="grid gap-x-10 gap-y-7 md:grid-cols-2 text-[12px] leading-relaxed text-slate-600 dark:text-slate-400">
                    {DISCLAIMER_SECTIONS.map((section) => (
                        <div key={section.title}>
                            <dt className="font-semibold text-slate-800 dark:text-slate-200 mb-1.5 tracking-tight">
                                {section.title}
                            </dt>
                            <dd>{section.body}</dd>
                        </div>
                    ))}
                </dl>

                <div className="mt-10 pt-6 border-t border-slate-300/70 dark:border-slate-800/70 flex flex-col sm:flex-row items-center justify-between gap-2">
                    <p className="text-[11px] text-slate-500 dark:text-slate-500">
                        © {year}{' '}
                        <span className="brand-wordmark">
                            Forge <span className="brand-accent">Ultra</span>
                        </span>{' '}
                        · A proprietary tool of Ki-Wealth | Founded & Developed by Irina Kainz, MBA, FRM.
                    </p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-500">
                        For informational and educational purposes only.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default ForgeUltraFooter;
